// src/components/CoverPage/TokenSaleAdmin.jsx
import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import Button from "./Button";

const TokenSaleAdmin = ({ 
  provider, 
  tokenContract, 
  tokenData, 
  refreshTokenSaleData, 
  isRefreshing,
  tiers,
  currentTier
}) => {
  // Admin panel states
  const [contractBalance, setContractBalance] = useState("0");
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawalStatus, setWithdrawalStatus] = useState("");
  const [adminActionStatus, setAdminActionStatus] = useState("");
  const [isStartingSale, setIsStartingSale] = useState(false);
  const [isEndingSale, setIsEndingSale] = useState(false);
  
  // Tier editing states
  const [showTierEditor, setShowTierEditor] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [newTierThreshold, setNewTierThreshold] = useState("");
  const [newTierPrice, setNewTierPrice] = useState("");
  const [isSavingTier, setIsSavingTier] = useState(false);

  // Get contract balance on mount and when refreshed
  useEffect(() => {
    refreshContractBalance();
  }, [tokenContract, provider]);

  // Helper to format ETH price for display
  const formatEthPrice = (priceInWei) => {
    if (!priceInWei) return "0 ETH";
    
    const ethPrice = ethers.utils.formatEther(priceInWei);
    const numericValue = parseFloat(ethPrice);
    
    // Format based on size
    if (numericValue >= 1) {
      return `${numericValue.toFixed(2)} ETH`;
    } else if (numericValue >= 0.01) {
      return `${numericValue.toFixed(4)} ETH`;
    } else {
      return `${numericValue.toFixed(6)} ETH`;
    }
  };
  
  // Function to refresh contract balance
  const refreshContractBalance = async () => {
    if (provider && tokenContract) {
      try {
        const balance = await provider.getBalance(tokenContract.address);
        setContractBalance(ethers.utils.formatEther(balance));
      } catch (error) {
        console.error("Error refreshing contract balance:", error);
      }
    }
  };
  
  // Function to handle withdrawal of ETH from the contract by the owner
  const handleWithdraw = async () => {
    if (!tokenContract || !withdrawalAmount) return;
    
    setIsWithdrawing(true);
    setWithdrawalStatus("");
    
    try {
      const amountWei = ethers.utils.parseEther(withdrawalAmount);
      console.log(`Withdrawing ${withdrawalAmount} ETH...`);
      
      const tx = await tokenContract.withdrawSaleProceeds(amountWei);
      console.log("Withdrawal transaction submitted:", tx.hash);
      
      setWithdrawalStatus("Transaction submitted. Waiting for confirmation...");
      
      await tx.wait();
      console.log("Withdrawal confirmed");
      
      setWithdrawalStatus(`Successfully withdrew ${withdrawalAmount} ETH`);
      setWithdrawalAmount("");
      
      // Refresh balance after withdrawal
      refreshContractBalance();
      
    } catch (error) {
      console.error("Error withdrawing:", error);
      setWithdrawalStatus(`Error: ${error.message || "Transaction failed"}`);
    } finally {
      setIsWithdrawing(false);
    }
  };
  
  // Handler for starting the token sale
  const handleStartSale = async () => {
    if (!tokenContract) return;
    
    setIsStartingSale(true);
    setAdminActionStatus("");
    
    try {
      console.log("Starting token sale...");
      
      const tx = await tokenContract.startInitialSale();
      console.log("Start sale transaction submitted:", tx.hash);
      
      setAdminActionStatus("Transaction submitted. Waiting for confirmation...");
      
      await tx.wait();
      console.log("Start sale transaction confirmed");
      
      setAdminActionStatus(`Token sale successfully started!`);
      
      // Refresh data
      setTimeout(() => {
        refreshTokenSaleData();
      }, 1000);
      
    } catch (error) {
      console.error("Error starting sale:", error);
      setAdminActionStatus(`Error: ${error.message || "Transaction failed"}`);
    } finally {
      setIsStartingSale(false);
    }
  };
  
  // Handler for ending the token sale
  const handleEndSale = async () => {
    if (!tokenContract) return;
    
    setIsEndingSale(true);
    setAdminActionStatus("");
    
    try {
      console.log("Ending token sale...");
      
      const tx = await tokenContract.endInitialSale();
      console.log("End sale transaction submitted:", tx.hash);
      
      setAdminActionStatus("Transaction submitted. Waiting for confirmation...");
      
      await tx.wait();
      console.log("End sale transaction confirmed");
      
      setAdminActionStatus(`Token sale successfully ended!`);
      
      // Refresh data
      setTimeout(() => {
        refreshTokenSaleData();
      }, 1000);
      
    } catch (error) {
      console.error("Error ending sale:", error);
      setAdminActionStatus(`Error: ${error.message || "Transaction failed"}`);
    } finally {
      setIsEndingSale(false);
    }
  };
  
  // Handler to add or update a tier
  const handleSaveTier = async () => {
    if (!tokenContract || !newTierThreshold || !newTierPrice) return;
    
    setIsSavingTier(true);
    setAdminActionStatus("");
    
    try {
      const thresholdWei = ethers.utils.parseEther(newTierThreshold);
      
      // Convert price from ETH to wei
      const priceWei = ethers.utils.parseEther(newTierPrice);
      
      if (editingTier === null) {
        // Adding a new tier
        console.log(`Adding new tier at ${newTierThreshold} tokens with price ${newTierPrice} ETH...`);
        
        const tx = await tokenContract.addPriceTier(thresholdWei, priceWei);
        console.log("Add tier transaction submitted:", tx.hash);
        
        setAdminActionStatus("Transaction submitted. Waiting for confirmation...");
        
        await tx.wait();
        console.log("Add tier transaction confirmed");
        
        setAdminActionStatus(`New price tier successfully added!`);
      } else {
        // Updating an existing tier
        console.log(`Updating tier ${editingTier} to ${newTierThreshold} tokens with price ${newTierPrice} ETH...`);
        
        const tx = await tokenContract.updatePriceTier(editingTier, thresholdWei, priceWei);
        console.log("Update tier transaction submitted:", tx.hash);
        
        setAdminActionStatus("Transaction submitted. Waiting for confirmation...");
        
        await tx.wait();
        console.log("Update tier transaction confirmed");
        
        setAdminActionStatus(`Price tier successfully updated!`);
      }
      
      // Reset the form
      setNewTierThreshold("");
      setNewTierPrice("");
      setEditingTier(null);
      setShowTierEditor(false);
      
      // Refresh data
      setTimeout(() => {
        refreshTokenSaleData();
      }, 1000);
      
    } catch (error) {
      console.error("Error saving tier:", error);
      setAdminActionStatus(`Error: ${error.message || "Transaction failed"}`);
    } finally {
      setIsSavingTier(false);
    }
  };
  
  // Handler to edit a tier
  const handleEditTier = (index) => {
    if (index >= 0 && index < tiers.length) {
      setEditingTier(index);
      setNewTierThreshold(tiers[index].threshold.toString());
      setNewTierPrice(ethers.utils.formatEther(tiers[index].price));
      setShowTierEditor(true);
    }
  };
  
  // Handler to remove a tier
  const handleRemoveTier = async (index) => {
    if (!tokenContract) return;
    
    setAdminActionStatus("");
    
    try {
      console.log(`Removing tier ${index}...`);
      
      const tx = await tokenContract.removePriceTier(index);
      console.log("Remove tier transaction submitted:", tx.hash);
      
      setAdminActionStatus("Transaction submitted. Waiting for confirmation...");
      
      await tx.wait();
      console.log("Remove tier transaction confirmed");
      
      setAdminActionStatus(`Price tier successfully removed!`);
      
      // Refresh data
      setTimeout(() => {
        refreshTokenSaleData();
      }, 1000);
      
    } catch (error) {
      console.error("Error removing tier:", error);
      setAdminActionStatus(`Error: ${error.message || "Transaction failed"}`);
    }
  };

  return (
    <div className="mt-12 border border-yellow-500 rounded-lg p-6 bg-gray-900">
      <h3 className="text-xl font-semibold mb-4 text-yellow-500">Owner Admin Panel</h3>
      
      {/* ETH Balance and Withdrawal */}
      <div className="mb-6 p-4 bg-gray-800 rounded-lg">
        <h4 className="text-lg font-medium mb-3 text-yellow-400">Treasury Management</h4>
        
        <div className="flex items-center justify-between mb-4">
          <span className="font-medium text-white">Contract Balance:</span>
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-white">{contractBalance} ETH</span>
            <button 
              onClick={refreshContractBalance}
              className="p-1 bg-yellow-700 rounded hover:bg-yellow-600 text-white"
              title="Refresh Balance"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-white">
            Withdrawal Amount (ETH)
          </label>
          <div className="flex space-x-2">
            <input
              type="number"
              value={withdrawalAmount}
              onChange={(e) => setWithdrawalAmount(e.target.value)}
              placeholder="Enter amount to withdraw"
              className="p-3 rounded-lg flex-grow text-black"
              min="0.000001"
              max={contractBalance}
              step="0.01"
              disabled={isWithdrawing}
            />
            <button
              onClick={() => setWithdrawalAmount(contractBalance)}
              className="px-3 py-1 bg-yellow-600 rounded-md text-white hover:bg-yellow-500"
              disabled={isWithdrawing}
            >
              Max
            </button>
          </div>
        </div>
        
        <button
          onClick={handleWithdraw}
          className="w-full bg-yellow-600 text-white py-2 rounded-md hover:bg-yellow-500 disabled:bg-gray-600"
          disabled={isWithdrawing || !withdrawalAmount || parseFloat(withdrawalAmount) <= 0 || parseFloat(withdrawalAmount) > parseFloat(contractBalance)}
        >
          {isWithdrawing ? "Processing Withdrawal..." : "Withdraw ETH"}
        </button>
        
        {withdrawalStatus && (
          <div className={`mt-3 p-3 rounded text-sm ${
            withdrawalStatus.startsWith("Error") ? "bg-red-900 text-red-200" : 
            withdrawalStatus.startsWith("Success") ? "bg-green-900 text-green-200" : 
            "bg-blue-900 text-blue-200"
          }`}>
            {withdrawalStatus}
          </div>
        )}
      </div>
      
      {/* Sale Controls */}
      <div className="mb-6 p-4 bg-gray-800 rounded-lg">
        <h4 className="text-lg font-medium mb-3 text-yellow-400">Sale Controls</h4>
        
        <div className="flex space-x-4">
          <button
            onClick={handleStartSale}
            className={`flex-1 py-2 rounded-md ${
              tokenData.initialSaleActive 
                ? "bg-gray-600 cursor-not-allowed text-gray-400" 
                : "bg-green-600 hover:bg-green-500 text-white"
            }`}
            disabled={tokenData.initialSaleActive || isStartingSale || isEndingSale}
          >
            {isStartingSale ? "Starting..." : "Start Sale"}
          </button>
          
          <button
            onClick={handleEndSale}
            className={`flex-1 py-2 rounded-md ${
              !tokenData.initialSaleActive 
                ? "bg-gray-600 cursor-not-allowed text-gray-400" 
                : "bg-red-600 hover:bg-red-500 text-white"
            }`}
            disabled={!tokenData.initialSaleActive || isStartingSale || isEndingSale}
          >
            {isEndingSale ? "Ending..." : "End Sale"}
          </button>
          
          <button
            onClick={refreshTokenSaleData}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md"
            disabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing..." : "Refresh Data"}
          </button>
        </div>
        
        {adminActionStatus && (
          <div className={`mt-3 p-3 rounded text-sm ${
            adminActionStatus.startsWith("Error") ? "bg-red-900 text-red-200" : 
            adminActionStatus.startsWith("Success") ? "bg-green-900 text-green-200" : 
            "bg-blue-900 text-blue-200"
          }`}>
            {adminActionStatus}
          </div>
        )}
      </div>
      
      {/* Price Tier Management */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-lg font-medium text-yellow-400">Price Tiers</h4>
          
          <button
            onClick={() => {
              setEditingTier(null);
              setNewTierThreshold("");
              setNewTierPrice("");
              setShowTierEditor(true);
            }}
            className="px-3 py-1 bg-green-600 hover:bg-green-500 text-white text-sm rounded-md"
          >
            Add New Tier
          </button>
        </div>
        
        {/* Tier List */}
        <div className="mt-2 space-y-2">
          {tiers.map((tier, index) => (
            <div 
              key={`tier-admin-${index}`}
              className={`p-3 rounded-md ${
                currentTier === index ? "bg-yellow-900 bg-opacity-30 border border-yellow-500" : "bg-gray-700"
              }`}
            >
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-white font-medium">Tier {index + 1}:</span>
                  <span className="ml-2 text-white">{tier.threshold.toLocaleString()} tokens at {formatEthPrice(tier.price)}</span>
                  {currentTier === index && (
                    <span className="ml-2 text-yellow-300 text-xs font-bold">CURRENT</span>
                  )}
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditTier(index)}
                    className="p-1 bg-blue-600 hover:bg-blue-500 text-white rounded"
                    title="Edit Tier"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  
                  {tiers.length > 1 && (
                    <button
                      onClick={() => handleRemoveTier(index)}
                      className="p-1 bg-red-600 hover:bg-red-500 text-white rounded"
                      title="Remove Tier"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Tier Editor */}
        {showTierEditor && (
          <div className="mt-4 p-4 bg-gray-700 rounded-lg">
            <h5 className="font-medium text-yellow-400 mb-3">
              {editingTier === null ? "Add New Price Tier" : `Edit Tier ${editingTier + 1}`}
            </h5>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1 text-white">
                  Token Threshold
                </label>
                <input
                  type="number"
                  value={newTierThreshold}
                  onChange={(e) => setNewTierThreshold(e.target.value)}
                  placeholder="Number of tokens (e.g. 100000)"
                  className="p-3 rounded-lg w-full text-black"
                  min="1"
                  disabled={isSavingTier}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-white">
                  Price per Token (ETH)
                </label>
                <input
                  type="number"
                  value={newTierPrice}
                  onChange={(e) => setNewTierPrice(e.target.value)}
                  placeholder="Price in ETH (e.g. 0.05)"
                  className="p-3 rounded-lg w-full text-black"
                  min="0.000001"
                  step="0.000001"
                  disabled={isSavingTier}
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleSaveTier}
                  className="flex-1 py-2 bg-green-600 hover:bg-green-500 text-white rounded-md disabled:bg-gray-600 disabled:text-gray-400"
                  disabled={isSavingTier || !newTierThreshold || !newTierPrice}
                >
                  {isSavingTier ? "Saving..." : editingTier === null ? "Add Tier" : "Save Changes"}
                </button>
                
                <button
                  onClick={() => setShowTierEditor(false)}
                  className="flex-1 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md"
                  disabled={isSavingTier}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TokenSaleAdmin;